// pages/InvoiceInfoPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, TextField, Autocomplete, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox,
  Chip, Tabs, Tab, Snackbar, Alert, IconButton, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  InputAdornment, Divider, Paper, alpha, useTheme, useMediaQuery,
} from '@mui/material';
import {
  ReceiptLong as InvoiceIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  DeleteOutline as DeleteIcon,
  Search as SearchIcon,
  Storefront as StorefrontIcon,
  PictureAsPdf as PdfIcon,
  AddCircleOutline as GenerateIcon,
  History as HistoryIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// ── Types ──────────────────────────────────────────────────────────────────
interface Seller {
  seller_info_id: number;
  seller_name: string;
  seller_type: string;
  mobile: string;
  address?: string | null;
}
interface SoldProduct {
  selling_info_id: number;
  product_id: number;
  product_name: string;
  sell_rate: number;
  quantity_pcs: number;
  litre_per_unit: number;   // ← NEW
  total_litre: number;      // ← NEW
  total_sales_price: number;
  sell_type: string;
  is_invoiced: boolean;
  invoice_code: string | null;
}
interface InvoiceRow {
  invoice_info_id: number;
  invoice_code: string;
  invoice_date: string;
  seller_name: string | null;
  shop_name: string | null;
  total_quantity: number;
  total_litre: number;      
  total_carton: number; 
  total_amount: string;
  created_at: string;
  created_by: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────
const PRIMARY = '#667eea';
const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const todayStr = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const money = (v: number | string) =>
  `Tk.  ${Number(v || 0).toLocaleString('en-BD', { maximumFractionDigits: 0 })}`;

const fmtDate = (s: string) => {
  if (!s) return '';

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  // Format 1: YYYY-MM-DD
  // Example: 2026-03-05
  let match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (match) {
    const [, year, month, day] = match;
    const monthIndex = Number(month) - 1;

    if (monthIndex >= 0 && monthIndex <= 11) {
      return `${day} ${months[monthIndex]} ${year}`;
    }
  }

  // Format 2: DD-MM-YYYY
  // Example: 05-03-2026
  match = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);

  if (match) {
    const [, day, month, year] = match;
    const monthIndex = Number(month) - 1;

    if (monthIndex >= 0 && monthIndex <= 11) {
      return `${day} ${months[monthIndex]} ${year}`;
    }
  }

  return s;
};

const fmtLitre = (v: number | null | undefined) => {
  const n = Number(v ?? 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const fmtCarton = (v: number | null | undefined) => {
  if (v == null) return '—';
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const InvoiceInfoPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState(0);

  // create state
  const [date, setDate] = useState(todayStr());
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [seller, setSeller] = useState<Seller | null>(null);
  const [products, setProducts] = useState<SoldProduct[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirmGen, setConfirmGen] = useState(false);

  // history state
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [loadingInv, setLoadingInv] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [deleteCode, setDeleteCode] = useState<string | null>(null);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' | 'info' }>({
    open: false, msg: '', sev: 'success',
  });
  const toast = (msg: string, sev: 'success' | 'error' | 'info' = 'success') =>
    setSnack({ open: true, msg, sev });

  // ── Data loaders ───────────────────────────────────────────────────────────
  useEffect(() => {
    apiService
      .get<{ success: boolean; results: Seller[] }>(`/trades/sellers/`)
      .then((r) => setSellers(r.results || []))
      .catch(() => toast('Failed to load sellers.', 'error'));
  }, []);

  const loadProducts = useCallback(async () => {
    if (!date || !seller) {
      setProducts([]); setSelected(new Set());
      return;
    }
    setLoadingProducts(true);
    try {
      const r = await apiService.get<{ success: boolean; results: SoldProduct[] }>(
        `/trades/invoice-sold-products/`,
        { params: { invoice_date: date, seller_info_id: seller.seller_info_id } },
      );
      setProducts(r.results || []);
      setSelected(new Set());
    } catch {
      toast('Failed to load sold products.', 'error');
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [date, seller]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const loadInvoices = useCallback(async () => {
    setLoadingInv(true);
    try {
      const r = await apiService.get<{ success: boolean; results: InvoiceRow[] }>(
        `/trades/invoices/`,
        { params: historySearch ? { search: historySearch } : {} },
      );
      setInvoices(r.results || []);
    } catch {
      toast('Failed to load invoices.', 'error');
    } finally {
      setLoadingInv(false);
    }
  }, [historySearch]);

  useEffect(() => { if (tab === 1) loadInvoices(); }, [tab, loadInvoices]);

  // ── Selection ──────────────────────────────────────────────────────────────
  const selectable = useMemo(() => products.filter((p) => !p.is_invoiced), [products]);
  const allSelected = selectable.length > 0 && selectable.every((p) => selected.has(p.selling_info_id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectable.map((p) => p.selling_info_id)));
  };

  const selectedTotal = useMemo(
    () => products.filter((p) => selected.has(p.selling_info_id)).reduce((s, p) => s + (p.total_sales_price || 0), 0),
    [products, selected],
  );

  // ── Actions ────────────────────────────────────────────────────────────────
  const download = async (code: string) => {
    try {
      await apiService.download(`/trades/invoices/${encodeURIComponent(code)}/download/`, `Invoice_${code}.pdf`);
    } catch {
      toast(`Could not download ${code}.`, 'error');
    }
  };

  const handleGenerate = async () => {
    if (!seller || selected.size === 0) return;
    setGenerating(true);
    try {
      const r = await apiService.post<{ success: boolean; message: string; data: InvoiceRow }>(
        `/trades/invoices/`,
        { invoice_date: date, seller_info_id: seller.seller_info_id, selling_info_ids: Array.from(selected) },
      );
      const code = r?.data?.invoice_code;
      toast(`Invoice ${code} generated.`, 'success');
      setConfirmGen(false);
      await loadProducts();           // refresh → invoiced rows now locked
      if (code) await download(code); // auto-download PDF
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        'Could not generate invoice. Some products may already be invoiced — refresh and retry.';
      toast(msg, 'error');
      setConfirmGen(false);
      loadProducts();
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCode) return;
    try {
      await apiService.delete<{ success: boolean }>(`/trades/invoices/${encodeURIComponent(deleteCode)}/`);
      toast(`Invoice ${deleteCode} deleted.`, 'info');
      setDeleteCode(null);
      loadInvoices();
    } catch {
      toast('Could not delete invoice.', 'error');
      setDeleteCode(null);
    }
  };

  // ── UI pieces ────────────────────────────────────────────────────────────
  const headerCard = (
    <Card elevation={0} sx={{ mb: 3, borderRadius: 3, background: GRADIENT, color: '#fff', overflow: 'hidden' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: 2, display: 'grid', placeItems: 'center', background: alpha('#fff', 0.18) }}>
          <InvoiceIcon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Invoice Management</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Generate invoices from sold products and download anytime by code
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const createPanel = (
    <Stack spacing={3}>
      {/* Filters */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(PRIMARY, 0.12)}` }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label="Invoice Date"
              type="date"
              size="small"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
            />
            <Autocomplete
              options={sellers}
              value={seller}
              onChange={(_, v) => setSeller(v)}
              getOptionLabel={(o) => o.seller_name}
              isOptionEqualToValue={(o, v) => o.seller_info_id === v.seller_info_id}
              sx={{ flex: 1, minWidth: { xs: '100%', sm: 260 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Seller"
                  placeholder="Search seller…"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start"><StorefrontIcon fontSize="small" sx={{ color: PRIMARY }} /></InputAdornment>
                    ),
                  }}
                />
              )}
              renderOption={(props, o) => (
                <li {...props} key={o.seller_info_id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.seller_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.seller_type} · {o.mobile}</Typography>
                  </Box>
                </li>
              )}
            />
            <Tooltip title="Reload sold products">
              <span>
                <IconButton onClick={loadProducts} disabled={!seller} sx={{ color: PRIMARY }}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {/* Products grid */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(PRIMARY, 0.12)}` }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Sold Products</Typography>
            {selectable.length > 0 && (
              <Chip size="small" label={`${selected.size} / ${selectable.length} selected`}
                sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY }} />
            )}
          </Box>
          <Divider />

          {loadingProducts ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : !seller ? (
            <EmptyState icon={<StorefrontIcon />} text="Pick a date and seller to load sold products." />
          ) : products.length === 0 ? (
            <EmptyState icon={<InvoiceIcon />} text="No sales found for this seller on the selected date." />
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleAll}
                        disabled={selectable.length === 0}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>}
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Pcs / Litre</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Rate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((p) => {
                    const checked = selected.has(p.selling_info_id);
                    return (
                      <TableRow
                        key={p.selling_info_id}
                        hover={!p.is_invoiced}
                        onClick={() => !p.is_invoiced && toggleOne(p.selling_info_id)}
                        sx={{
                          cursor: p.is_invoiced ? 'default' : 'pointer',
                          opacity: p.is_invoiced ? 0.55 : 1,
                          bgcolor: checked ? alpha(PRIMARY, 0.06) : 'transparent',
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={checked} disabled={p.is_invoiced} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.product_name}</Typography>
                          {p.is_invoiced && (
                            <Chip size="small" icon={<CheckIcon sx={{ fontSize: 13 }} />} label={p.invoice_code || 'Invoiced'}
                              sx={{ mt: 0.5, height: 20, fontSize: '0.65rem', fontWeight: 700,
                                    bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32' }} />
                          )}
                          {isMobile && (
                            <Typography variant="caption" color="text.secondary" display="block">{p.sell_type}</Typography>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <TableCell><Chip size="small" label={p.sell_type} variant="outlined" sx={{ height: 22 }} /></TableCell>
                        )}
                        <TableCell align="center">{p.quantity_pcs} / {fmtLitre(p.total_litre)} L</TableCell>
                        <TableCell align="right">{money(p.sell_rate)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{money(p.total_sales_price)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Sticky action bar */}
      {products.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'sticky', bottom: 16, borderRadius: 3, p: 2,
            display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2,
            border: `1px solid ${alpha(PRIMARY, 0.15)}`,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">Selected total</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: PRIMARY }}>{money(selectedTotal)}</Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" disabled={selected.size === 0} onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <Button
              variant="contained"
              startIcon={<GenerateIcon />}
              disabled={selected.size === 0 || generating}
              onClick={() => setConfirmGen(true)}
              sx={{ background: GRADIENT, fontWeight: 700, px: 3 }}
            >
              Generate Invoice
            </Button>
          </Stack>
        </Paper>
      )}
    </Stack>
  );

  const historyPanel = (
    <Stack spacing={3}>
      {/* Download by code */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(PRIMARY, 0.12)}` }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Download by Invoice Code</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              placeholder="e.g. INV-20260512-0001"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.trim())}
              onKeyDown={(e) => e.key === 'Enter' && codeInput && download(codeInput)}
              InputProps={{ startAdornment: <InputAdornment position="start"><PdfIcon fontSize="small" sx={{ color: PRIMARY }} /></InputAdornment> }}
            />
            <Button
              variant="contained" startIcon={<DownloadIcon />} disabled={!codeInput}
              onClick={() => download(codeInput)}
              sx={{ background: GRADIENT, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              Download
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* List */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(PRIMARY, 0.12)}` }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Invoice History</Typography>
            <TextField
              size="small"
              placeholder="Search code…"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ width: { xs: '100%', sm: 220 } }}
            />
            <Tooltip title="Refresh"><IconButton onClick={loadInvoices} sx={{ color: PRIMARY }}><RefreshIcon /></IconButton></Tooltip>
          </Box>
          <Divider />
          {loadingInv ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : invoices.length === 0 ? (
            <EmptyState icon={<HistoryIcon />} text="No invoices generated yet." />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Invoice Code</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Selling Date</TableCell>}
                    <TableCell sx={{ fontWeight: 700, }}>Seller Name</TableCell>
                    {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>Pcs / Litre</TableCell>}
                    {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>Carton</TableCell>}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.invoice_info_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: PRIMARY }}>{inv.invoice_code}</Typography>
                        {isMobile && <Typography variant="caption" color="text.secondary">{fmtDate(inv.invoice_date)}</Typography>}
                      </TableCell>
                      {!isMobile && <TableCell>{fmtDate(inv.invoice_date)}</TableCell>}
                      <TableCell>{inv.shop_name || inv.seller_name || '—'}</TableCell>
                      {!isMobile && <TableCell align="center">{inv.total_quantity} / {fmtLitre(inv.total_litre)} L</TableCell>}
                      {!isMobile && <TableCell align="center">{fmtCarton(inv.total_carton)}</TableCell>}
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{money(inv.total_amount)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download PDF">
                          <IconButton size="small" onClick={() => download(inv.invoice_code)} sx={{ color: PRIMARY }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete invoice">
                          <IconButton size="small" onClick={() => setDeleteCode(inv.invoice_code)} sx={{ color: '#e53935' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );

  return (
    <Box>
      {headerCard}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' }, '& .Mui-selected': { color: PRIMARY } }}
        TabIndicatorProps={{ sx: { background: PRIMARY } }}
      >
        <Tab icon={<GenerateIcon />} iconPosition="start" label="Create Invoice" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Invoice History" />
      </Tabs>

      {tab === 0 ? createPanel : historyPanel}

      {/* Generate confirm */}
      <Dialog open={confirmGen} onClose={() => !generating && setConfirmGen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Generate Invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Creating an invoice for <b>{selected.size}</b> product{selected.size > 1 ? 's' : ''} (total <b>{money(selectedTotal)}</b>)
            for <b>{seller?.seller_name}</b> on <b>{fmtDate(date)}</b>.
            <br /><br />
            These products will be locked and can't be invoiced again for this date &amp; seller.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmGen(false)} disabled={generating}>Cancel</Button>
          <Button
            variant="contained" onClick={handleGenerate} disabled={generating}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <GenerateIcon />}
            sx={{ background: GRADIENT, fontWeight: 700 }}
          >
            {generating ? 'Generating…' : 'Generate & Download'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteCode} onClose={() => setDeleteCode(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete invoice {deleteCode}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes the invoice and releases its products so they can be invoiced again. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteCode(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const EmptyState: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <Box sx={{ display: 'grid', placeItems: 'center', py: 6, color: 'text.secondary', gap: 1 }}>
    <Box sx={{ fontSize: 40, opacity: 0.4, '& svg': { fontSize: 40 } }}>{icon}</Box>
    <Typography variant="body2">{text}</Typography>
  </Box>
);

export default InvoiceInfoPage;